/**
 * Full customer export for the guest list (2026-10-05).
 *
 * WHY THIS EXISTS — "Export" failed and, when it ran, it exported eleven columns.
 *  1. The tenant-wide op (`exportGuests`, POST /v1/admin/guests/export) is not served at design
 *     time: the kit seed `createGuestExportMswHandlers` exists but is not mounted, so the request
 *     fell through to a 404 ("Export failed"). Observed in the preview, 2026-10-05.
 *  2. Its file is the worker's fixed column set (name, email, phone, registered_at, status, ticket,
 *     source, event_id, company, segment, external_id). The guest read (`listGuests`, GuestDto)
 *     carries far more: first/last name, title, customerId, check-in time, waitlist position, tags,
 *     notes, pass code, VIP/press/unsubscribed flags, payment, and every booking-question answer in
 *     customFields. The customer directory (`listBookingCustomers`) adds the person's type,
 *     external id, locale/time zone, created/last-seen and their appointment / registration /
 *     check-in / queue counts.
 *
 * WHAT IT DOES — reads the SERVER, never the rows on screen: pages `listGuests` to the end, and,
 * when the role holds `customers.read`, pages the directory and joins each guest to their record
 * (customerId first, then email). Writes the worker's CSV dialect: UTF-8 BOM, CRLF records, a cell
 * quoted only when it holds a comma, quote or line break.
 */
import { listGuests } from "@v0y4ge/api-client";
import { listBookingCustomers, DIRECTORY_LIMIT_MAX } from "../customers/customers-data";
import { can } from "../../shell/access";
import { fetchEventsList } from "../../lib/api";

const GUEST_PAGE = 500;
const MAX_GUESTS = 50000; // safety cap, never a silent stop: hitting it sets `truncated`
const MAX_DIRECTORY = 5000;

type Row = Record<string, any>;

export interface FullExportResult { csv: string; rows: number; directoryJoined: boolean; directoryNote?: string; excludedNoConsent: number; masked: boolean;
  /** Guests with no linked customer record, so no consent on file; never exported. */
  excludedNoCustomer: number;
  /** Linked guests left out because their consent could not be confirmed (customer directory unread, denied, or the record missing). Fail closed. */
  consentUnconfirmed: number;
  /** The guest read hit the safety cap, so this file is PARTIAL; `totalGuests` is the server's count when it gave one. */
  truncated: boolean; totalGuests?: number }

/* Data model (Gold Master Part 13, decision 175; Part 1): an export is consent-filtered and
   pseudonymised by default; identity comes from the linked customer record, never from an
   email match. Unmasking is an explicit, logged choice by the caller.
   Pseudonymised means: direct identifiers (names, email, phone, notes, pass code, free-text answers
   and fields) are blanked, and every stable identifier (guest id, customer id, external ids) is
   dropped and replaced by one `guest_ref` token salted per export, so a person cannot be re-joined
   across files. It is NOT anonymous: quasi-identifiers (event, ticket, timestamps, company, tags,
   segments, locale, time zone, program, campus, class year, counts) stay, so a small group can still
   be singled out. Treat the file as personal data. */
const PII_COLS = new Set(["first_name", "last_name", "name", "email", "phone", "title", "pass_code", "notes", "external_id", "customer_external_id"]);
const ID_COLS = new Set(["guest_id", "customer_id", "external_id", "customer_external_id"]);
function exportSalt(): string { const b = new Uint8Array(16); crypto.getRandomValues(b); return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join(""); }
function fnv(seed: string): string { let h = 2166136261; for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h.toString(36); }
function pseudonym(seed: string): string { return "p_" + fnv(seed) + fnv("#" + seed); }

async function readGuestPages(eventId?: string): Promise<{ rows: Row[]; truncated: boolean; total?: number }> {
  const out: Row[] = [];
  let total: number | undefined;
  for (let page = 1; ; page++) {
    const res: any = await listGuests({ ...(eventId ? { eventId } : {}), page: String(page), limit: String(GUEST_PAGE) } as any);
    const batch: Row[] = res.guests ?? res.data ?? [];
    out.push(...batch);
    const t = res.total ?? res.pagination?.total;
    if (typeof t === "number") total = t;
    const more = res.hasMore ?? res.pagination?.hasMore ?? (typeof t === "number" ? out.length < t : batch.length === GUEST_PAGE);
    if (!more || batch.length === 0) return { rows: out, truncated: false, total };
    if (out.length >= MAX_GUESTS) return { rows: out, truncated: true, total };
  }
}

export async function readAllGuests(eventId?: string): Promise<Row[]> {
  return (await readGuestPages(eventId)).rows;
}

async function readDirectory(): Promise<{ byId: Map<string, Row>; byEmail: Map<string, Row> } | { denied: true } | null> {
  if (!can("customers.read")) return { denied: true };
  const byId = new Map<string, Row>(); const byEmail = new Map<string, Row>();
  try {
    let n = 0;
    for (let page = 1; n < MAX_DIRECTORY; page++) {
      const res: any = await listBookingCustomers({ page, limit: DIRECTORY_LIMIT_MAX });
      const batch: Row[] = res?.data ?? [];
      for (const r of batch) { byId.set(String(r.id), r); if (r.email) byEmail.set(String(r.email).toLowerCase(), r); }
      n += batch.length;
      if (!res?.pagination?.hasMore || batch.length === 0) break;
    }
    return { byId, byEmail };
  } catch (e: any) {
    if (e?.status === 403 || e?.status === 401) return { denied: true };
    return null;
  }
}

const text = (v: unknown): string => {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map(text).filter(Boolean).join("; ");
  if (typeof v === "object") { try { return JSON.stringify(v); } catch { return ""; } }
  return String(v);
};
/* Spreadsheet formula injection (CC, 2026-10-06): a value whose first character, after any leading
   whitespace or control characters, is = + - or @, or that starts with a tab or carriage return,
   would run as a formula when the CSV is opened. Prefix it with a single quote so it stays text.
   Plain numbers (money, counts, a negative refund) are left as they are. Applies to every column. */
const PLAIN_NUMBER = /^[+-]?\d+(\.\d+)?$/;
const neutralise = (s: string): string => {
  if (PLAIN_NUMBER.test(s)) return s;
  const lead = s.replace(/^[\s\u0000-\u001f]+/, "");
  return /^[=+\-@]/.test(lead) || /^[\t\r]/.test(s) ? "'" + s : s;
};
const cell = (v: unknown): string => { const s = neutralise(text(v)); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
const money = (minor: unknown) => (typeof minor === "number" ? (minor / 100).toFixed(2) : "");

/** Every scalar customField except the two structured blocks, plus every answer, as stable columns. */
function customColumns(guests: Row[]): { fields: string[]; answers: string[] } {
  const f = new Set<string>(); const a = new Set<string>();
  for (const g of guests) {
    const cf = (g.customFields ?? g.custom_fields ?? {}) as Row;
    for (const [k, v] of Object.entries(cf)) {
      if (k === "answers" && v && typeof v === "object") Object.keys(v as Row).forEach((q) => a.add(q));
      else if (k !== "payment" && (v == null || typeof v !== "object")) f.add(k);
    }
  }
  return { fields: [...f].sort(), answers: [...a].sort() };
}

export async function buildFullGuestExport(opts: { ids?: Set<string>; eventTitles?: Map<string, string>; eventId?: string; unmask?: boolean } = {}): Promise<FullExportResult> {
  const [read, dir] = await Promise.all([readGuestPages(opts.eventId), readDirectory()]);
  const allGuests = read.rows;
  const picked = opts.ids ? allGuests.filter((g) => opts.ids!.has(String(g.id))) : allGuests;
  const directory = dir && !("denied" in dir) ? dir : null;
  const linked = (g: Row) => (directory && g.customerId ? directory.byId.get(String(g.customerId)) ?? null : null);
  /* Consent filter (affirmative, the same rule as the server export): a row is exported ONLY when the
     guest is linked to a customer record AND that record's marketingConsent is true. Everything else is
     left out and counted: no customer link; a linked record that cannot be read (fail closed); a record
     that withholds or does not state consent; an unsubscribed guest. (CC, 2026-10-06) */
  const consented = (g: Row) => g.unsubscribed !== true && linked(g)?.marketingConsent === true;
  const noCustomer = (g: Row) => g.unsubscribed !== true && !g.customerId;
  const unconfirmed = (g: Row) => !!g.customerId && g.unsubscribed !== true && !linked(g);
  const guests = picked.filter(consented);
  const excludedNoCustomer = picked.filter(noCustomer).length;
  const consentUnconfirmed = picked.filter(unconfirmed).length;
  const excludedNoConsent = picked.length - guests.length - consentUnconfirmed - excludedNoCustomer;
  const { fields, answers } = customColumns(guests);
  /* Event titles: the caller's map, completed from the events read when it lacks any (the export
     came out with blank titles when the screen's events had not loaded yet — 2026-10-05). */
  const titles = new Map(opts.eventTitles ?? []);
  if (guests.some((g) => (g.eventId ?? g.event_id) && !titles.get(String(g.eventId ?? g.event_id)))) {
    try {
      for (let page = 1; page <= 10; page++) {
        const res: any = await fetchEventsList({ page, limit: 500 } as any);
        const list: Row[] = res?.events ?? res?.data ?? res?.items ?? [];
        for (const ev of list) if (ev?.id && !titles.get(String(ev.id))) titles.set(String(ev.id), String(ev.title ?? ev.name ?? ""));
        if (!(res?.hasMore ?? res?.pagination?.hasMore) || !list.length) break;
      }
    } catch { /* titles stay blank rather than invented */ }
  }

  const base: [string, (g: Row, c: Row | null) => unknown][] = [
    ["guest_id", (g) => g.id],
    ["first_name", (g) => g.firstName ?? g.first_name],
    ["last_name", (g) => g.lastName ?? g.last_name],
    ["name", (g) => g.name ?? [g.firstName ?? g.first_name, g.lastName ?? g.last_name].filter(Boolean).join(" ")],
    ["email", (g) => g.email],
    ["phone", (g) => g.phone],
    ["company", (g) => g.company ?? g.organization],
    ["title", (g) => g.title],
    ["status", (g) => g.status],
    ["waitlist_position", (g) => g.waitlistPosition],
    ["event_id", (g) => g.eventId ?? g.event_id],
    ["event_title", (g) => titles.get(String(g.eventId ?? g.event_id)) ?? ""],
    ["ticket_id", (g) => g.tierId],
    ["ticket", (g) => g.ticketName ?? g.ticket ?? ""], // JAI-1846: the name, never the tier reference
    ["registered_at", (g) => g.registeredAt],
    ["checked_in_at", (g) => g.checkedInAt],
    ["source", (g) => g.source],
    ["external_id", (g) => g.externalId],
    ["external_object_type", (g) => g.externalObjectType],
    ["segments", (g) => (g.segments?.length ? g.segments : g.segment ? [g.segment] : [])],
    ["tags", (g) => g.tags],
    ["vip", (g) => g.vip],
    ["press", (g) => g.press],
    ["unsubscribed", (g) => g.unsubscribed],
    ["pass_code", (g) => g.passCode],
    ["notes", (g) => g.notes],
    ["payment_status", (g) => (g.payment ?? g.customFields?.payment)?.status],
    ["payment_amount", (g) => money((g.payment ?? g.customFields?.payment)?.amountMinor ?? (g.payment ?? g.customFields?.payment)?.amount_minor)],
    ["payment_refunded", (g) => money((g.payment ?? g.customFields?.payment)?.refundedMinor ?? (g.payment ?? g.customFields?.payment)?.refunded_minor)],
    ["payment_currency", (g) => (g.payment ?? g.customFields?.payment)?.currency],
    ["payment_provider", (g) => (g.payment ?? g.customFields?.payment)?.provider],
  ];
  const directoryCols: [string, (g: Row, c: Row | null) => unknown][] = directory ? [
    ["customer_id", (g, c) => c?.id ?? g.customerId],
    ["customer_type", (_g, c) => c?.customerType?.name],
    ["customer_external_id", (_g, c) => c?.externalId?.value],
    ["locale", (_g, c) => c?.locale],
    ["timezone", (_g, c) => c?.timezone],
    ["location", (_g, c) => c?.location?.name],
    ["program", (_g, c) => c?.program],
    ["campus", (_g, c) => c?.campusOfRecord],
    ["class_year", (_g, c) => c?.classYear],
    ["marketing_consent", (_g, c) => c?.marketingConsent],
    ["customer_created_at", (_g, c) => c?.createdAt],
    ["last_seen_at", (_g, c) => c?.lastSeenAt],
    ["appointments", (_g, c) => c?.counts?.appointments],
    ["event_registrations", (_g, c) => c?.counts?.eventRegistrations],
    ["event_check_ins", (_g, c) => c?.counts?.eventCheckIns],
    ["queue_joins", (_g, c) => c?.counts?.queueJoins],
  ] : [["customer_id", (g) => g.customerId]];
  const custom: [string, (g: Row) => unknown][] = [
    ...fields.map((k) => ["field:" + k, (g: Row) => (g.customFields ?? g.custom_fields ?? {})[k]] as [string, (g: Row) => unknown]),
    ...answers.map((q) => ["answer:" + q, (g: Row) => ((g.customFields ?? g.custom_fields ?? {}).answers ?? {})[q]] as [string, (g: Row) => unknown]),
  ];
  const all = [...base, ...directoryCols, ...custom] as [string, (g: Row, c: Row | null) => unknown][];
  const salt = opts.unmask ? "" : exportSalt();
  const cols: [string, (g: Row, c: Row | null) => unknown][] = opts.unmask ? all : [["guest_ref", () => ""], ...all.filter(([h]) => !ID_COLS.has(h))];

  const lines = [cols.map(([h]) => cell(h)).join(",")];
  for (const g of guests) {
    const c = linked(g);
    const key = String(c?.id ?? g.customerId ?? g.id);
    lines.push(cols.map(([h, get]) => {
      if (opts.unmask) return cell(get(g, c));
      if (h === "guest_ref" || h === "name" || h === "email") return cell(pseudonym(salt + ":" + h + ":" + key));
      if (PII_COLS.has(h) || h.startsWith("answer:") || h.startsWith("field:")) return "";
      return cell(get(g, c));
    }).join(","));
  }
  return {
    csv: `\uFEFF${lines.join("\r\n")}\r\n`,
    rows: guests.length,
    directoryJoined: !!directory,
    excludedNoConsent,
    excludedNoCustomer,
    consentUnconfirmed,
    truncated: read.truncated,
    totalGuests: read.total,
    masked: !opts.unmask,
    directoryNote: [
      dir && "denied" in dir ? "Customer-directory fields need the customers.read permission, so they are not included." : !dir ? "The customer directory couldn't be read, so its fields are not included." : undefined,
      excludedNoCustomer ? `${excludedNoCustomer} guest${excludedNoCustomer === 1 ? "" : "s"} left out because they have no customer record, so no consent on file.` : undefined,
      consentUnconfirmed ? `${consentUnconfirmed} linked guest${consentUnconfirmed === 1 ? "" : "s"} left out because consent could not be confirmed.` : undefined,
      read.truncated ? `Partial export: stopped at ${read.rows.length} of ${read.total ?? "an unknown number of"} guests.` : undefined,
    ].filter(Boolean).join(" ") || undefined,
  };
}
